/* eslint-disable no-unused-expressions */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useContext,
} from 'react'
import Router from 'next/router'
import classNames from 'classnames'
import { sendTeaCommEvent } from 'lib/tea-analyze'
import { isNewVersion, VERSION_COOKIE_KEY } from 'lib/version'
import config, { IChild } from './config'
import PubSub from 'pubsub-js'
import SelectCarWrap from './components/select-car-wrap'
import ChildWrap, { childClickHandler, getLink } from './components/child-wrap'
import useHasTips from 'Components/hooks/use-top-tips'
import { GlobalStoreContext } from 'Components/store'
import { LoginStoreContext } from 'New/login/login-store'

interface IProps {
  fixType?: string
  pageType?: string
}

export default function FixNav({ fixType = '', pageType = '' }: IProps) {
  const isHasTips = useHasTips()
  const [url, setUrl] = useState('')

  // 2.0的页面 固定1280宽度 小于1440的时候隐藏
  const isAutoNav = pageType?.indexOf('auto-nav') > -1

  const { state: globalStore } = useContext(GlobalStoreContext)
  const { userData } = globalStore
  const { openLogin } = useContext(LoginStoreContext)

  const routeChangeComplete = useCallback((url: string) => {
    if (url === '') {
      setUrl('/')
      return
    }
    setUrl(url)
  }, [])

  const isActive = useCallback(
    (as: string | undefined, isActive, isNeedLogin = false) => {
      // 需要处理 url 后面的参数
      let realUrl = url
      if (realUrl?.indexOf('?') > -1) {
        realUrl = realUrl?.split('?')?.[0]
      }
      if (realUrl === as && (!isNeedLogin || userData?.user_id)) {
        return true
      }

      if (isActive && isActive(realUrl, userData?.user_id)) {
        return true
      }
      return false
    },
    [url, userData?.user_id]
  )

  useEffect(() => {
    setUrl(window?.location?.pathname || '/')
    Router.events.on('routeChangeComplete', routeChangeComplete)
    return () => {
      Router.events.off('routeChangeComplete', routeChangeComplete)
    }
  }, [])

  const [handleType, setHandleType] = useState(fixType)
  const handler = useCallback((type, value) => {
    if (fixType) {
      return
    }
    setHandleType(value)
  }, [])

  useEffect(() => {
    const token = PubSub.subscribe('SIDE_NAV_STATUS', handler)
    return () => {
      PubSub.unsubscribe(token)
    }
  }, [])

  const widthClassName = useMemo(() => {
    return `side-nav-width tw-bg-white ${
      isAutoNav ? 'tw-hidden lg:tw-block' : ''
    } ${handleType ? handleType : 'tw-w-88 lg:tw-w-160'}`
  }, [handleType, isAutoNav])

  const childClick = useCallback(
    (child: IChild) => {
      childClickHandler(child, userData?.user_id || '', openLogin)
    },
    [userData?.user_id, openLogin]
  )

  return (
    <div className={`global-side-nav ${widthClassName}`}>
      <div className={`side-nav-fix-wrap ${widthClassName}`}>
        <div
          className={`side-nav-scroll-wrap g-hover-scroll-bar ${widthClassName}`}
        >
          <ul className={widthClassName} onClick={(e) => e.stopPropagation()}>
            {config.map((item, index) => {
              return (
                <React.Fragment key={`fragment-${index}`}>
                  <li
                    id={`sideItemWrap_${item.id}`}
                    className={classNames('side-item-wrap', {
                      active: isActive(item.as, item?.isActive),
                      hasChildren: item.hoverComponent,
                    })}
                    key={`side-${index}`}
                  >
                    <a
                      className={classNames(
                        'side-item parent-side-item tw-h-72 tw-block lg:tw-h-55 tw-pt-17',
                        {
                          active: isActive(item.as, item?.isActive),
                        }
                      )}
                      onClick={(e) => {
                        e.stopPropagation()
                        sendTeaCommEvent('clk_event', {
                          event_id: 'common_menu_item',
                          button_name: item.name,
                        })
                        // eslint-disable-next-line no-unused-expressions
                        item.onClick?.()
                        if (
                          getLink(item?.link, item.newVersionLink) &&
                          item.as
                        ) {
                          Router.push(
                            getLink(item?.link, item.newVersionLink),
                            item.as
                          )
                        }
                      }}
                      onMouseEnter={() => {
                        // eslint-disable-next-line no-unused-expressions
                        item?.onMouseEnter?.()
                      }}
                      onMouseLeave={() => {
                        // eslint-disable-next-line no-unused-expressions
                        item?.onMouseLeave?.()
                      }}
                    >
                      {item.icon && (
                        <span className={`icon ${item.icon} tw-inline-block`} />
                      )}
                      <span className="name tw-block tw-text-center lg:tw-inline-block lg:tw-text-left">
                        {item.name}
                        {(item.hoverComponent || item.children) && (
                          <span
                            className={classNames('children-arrow', {
                              'tw-inline-block lg:tw-hidden': item.children,
                            })}
                          />
                        )}
                      </span>
                    </a>
                    {item?.children ? (
                      <ul className="side-nav-child-wrap">
                        {item?.children?.map((child: IChild, i: number) => {
                          return (
                            <li key={`side-child-${i}`}>
                              <a
                                className={classNames(
                                  'side-item tw-h-55 tw-hidden lg:tw-block',
                                  {
                                    active: isActive(
                                      child.as,
                                      child?.isActive,
                                      child?.isNeedLogin
                                    ),
                                  }
                                )}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  sendTeaCommEvent('clk_event', {
                                    event_id: 'common_menu_item',
                                    button_name: child.name,
                                  })
                                  childClick(child)
                                }}
                              >
                                <span
                                  className={`icon lg:tw-inline-block tw-hidden`}
                                />
                                <span className="name tw-block tw-text-center lg:tw-inline-block lg:tw-text-left tw-pt-17">
                                  {child.name}
                                </span>
                              </a>
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </li>
                </React.Fragment>
              )
            })}
          </ul>
        </div>
        <SelectCarWrap />
        <ChildWrap />
      </div>
      <style jsx>
        {`
          .side-nav-fix-wrap {
            min-height: calc(100vh - 84px);
            position: fixed;
            top: ${isHasTips ? '136px' : '84px'};
            z-index: 1999;
          }
          .side-nav-scroll-wrap {
            position: absolute;
            z-index: 10;
            width: 100%;
            height: 100%;
            left: 0;
            overflow-x: auto;
            overflow-y: scroll;
            overscroll-behavior: none;
          }
          .side-item {
            position: relative;
            text-align: center;
          }
          .side-item.active {
            background: #ffcc32;
          }
          .side-item:hover {
            background: #fff2ce;
            color: #1f2129;
            cursor: pointer;
          }
          .side-item.active:hover {
            background: #ffcc32;
          }
          .name {
            width: 88px;
            padding-left: 5px;
            padding-right: 5px;
            margin: 0 auto;
            position: relative;
          }
          .side-item:hover {
            color: #1f2129;
          }
          .side-nav-width.on {
            width: 160px !important;
          }
          .side-nav-width.on .name {
            display: inline-block !important;
            text-align: left !important;
          }
          .side-nav-width.on .side-item {
            height: 55px !important;
          }

          .side-nav-width.off .side-item {
            height: 72px !important;
          }
          .side-nav-width.off .side-nav-child-wrap .side-item {
            height: 55px !important;
            line-height: 55px;
            width: 112px;
          }
          .side-nav-width.off .side-nav-child-wrap .name {
            padding: 0;
          }

          .side-nav-width.off .name {
            display: block !important;
            text-align: center !important;
          }
          .side-nav-width.off {
            width: 88px !important;
          }

          .icon {
            width: 18px;
            height: 18px;
            vertical-align: text-bottom;
            margin-right: 0px;
          }
          .icon.feed {
            background: url(${require('./static/feed.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.feedback {
            background: url(${require('./static/feedback.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.home {
            background: url(${require('./static/home.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.live {
            background: url(${require('./static/live.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.fans {
            background: url(${require('./static/fans.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.me {
            background: url(${require('./static/me.png')}) no-repeat 0 0 / cover;
          }
          .icon.picture {
            background: url(${require('./static/picture.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.rank {
            background: url(${require('./static/rank.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.select-car {
            background: url(${require('./static/select-car.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.tool {
            background: url(${require('./static/tool.png')}) no-repeat 0 0 /
              cover;
          }
          .icon.video {
            background: url(${require('./static/video.png')}) no-repeat 0 0 /
              cover;
          }

          .child {
            color: #606370;
          }

          .children-arrow {
            width: 0;
            height: 0;
            position: absolute;
            margin-top: 7px;
            margin-left: 5px;
            border-top: 4px solid transparent;
            border-bottom: 4px solid transparent;
            border-left: 5px solid #c9cbd6;
          }
          .side-nav-width.off .children-arrow {
            display: inline-block;
          }
          .side-item:hover .children-arrow {
            border-left: 5px solid #1f2129;
          }
          .side-item.active .children-arrow {
            border-left: 5px solid #1f2129;
          }

          .side-nav-child-wrap {
            text-align: center;
          }
          .side-nav-width.on .side-nav-child-wrap .side-item {
            display: block;
          }

          .side-nav-width.off .side-nav-child-wrap .side-item {
            display: none;
          }
          .side-item-wrap {
            position: relative;
          }
          .side-item-wrap:hover .side-item {
            display: block !important;
          }

          .global-side-nav.off .side-nav-child-wrap {
            position: absolute;
            left: 88px;
            top: 0;
            width: 112px;
            background: #fff;
          }
        `}
      </style>
    </div>
  )
}
